// src/screens/academique/MesCours.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  CardActions,
  Paper,
  LinearProgress,
  IconButton,
  Container,
  Snackbar,
  Alert,
  Button
} from '@mui/material';

import {
  Search as SearchIcon,
  Book as BookIcon,
  MenuBook as MenuBookIcon,
  Description as DescriptionIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Bookmark as BookmarkIcon,
  PictureAsPdf as PdfIcon,
  Slideshow as PptIcon,
  VideoLibrary as VideoIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Stars as StarsIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import ImportContactsIcon from '@mui/icons-material/ImportContacts';
import FormationService from '../../services/FormationService';
import CoursService from '../../services/CoursService';
import CourseViewer from './CourseViewer';
import QcmModal from './QcmModal';
import FormationMainContainer from '../../components/FormationMainContainer';
import { headerBanqueCV } from '../../data/modules/banque-cv-header';

const MesCours = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMatiere, setSelectedMatiere] = useState('');
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [qcmResults, setQcmResults] = useState([]);
  const [matieres, setMatieres] = useState([]);
  const [studentId, setStudentId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 3;
  
  // États pour les notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Modals state
  const [courseViewerOpen, setCourseViewerOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [qcmModalOpen, setQcmModalOpen] = useState(false);
  const [selectedCourseForQcm, setSelectedCourseForQcm] = useState(null);

  // Récupérer l'ID de l'étudiant depuis localStorage
  useEffect(() => {
    const getUserData = () => {
      const storedUserDetails = localStorage.getItem('USER_DETAILS');
      if (storedUserDetails && storedUserDetails !== 'undefined' && storedUserDetails !== 'null') {
        try {
          const userData = JSON.parse(storedUserDetails);
          return userData.person_id || userData.user_id;
        } catch (parseError) {
          return null;
        }
      }
      return null;
    };
    
    const id = getUserData();
    setStudentId(id);
  }, []);

  // Charger les données
  useEffect(() => {
    const fetchData = async () => {
      if (!studentId) return;
      
      try {
        setLoading(true);
        
        // Récupérer les matières avec leurs cours
        const matieresResult = await FormationService.getMatieres(studentId);
        if (matieresResult && matieresResult.status === true) {
          setMatieres(matieresResult.data || []);
          
          // Extraire tous les cours des matières
          const allCourses = [];
          (matieresResult.data || []).forEach(matiere => {
            if (matiere.cours && matiere.cours.length) {
              matiere.cours.forEach(course => {
                allCourses.push({
                  ...course,
                  matiere_nom: matiere.nom,
                  matiere_id: matiere.id
                });
              });
            }
          });
          setCourses(allCourses);
        }
        
        // Récupérer les résultats QCM
        const resultsResult = await FormationService.getResults(studentId);
        if (resultsResult && resultsResult.status === true) {
          setQcmResults(resultsResult.data?.results || []);
        }
        
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [studentId]);

  /**
   * Enregistrer l'historique de visualisation d'un cours
   * @param {number} courseId - ID du cours
   * @param {string} action - Action effectuée ('View' ou 'Download')
   */
  const recordHistory = async (courseId, action = 'View') => {
    try {
      const historyData = {
        cours_id: courseId,
        action: action
      };
      const result = await CoursService.createHistory(historyData);
      if (result && result.status === true) {
      }
    } catch (error) {
    }
  };

  /**
   * Ouvrir un cours et enregistrer l'historique
   * @param {Object} course - Le cours à ouvrir
   */
  const openCourse = async (course) => {
    setSelectedCourse(course);
    setCourseViewerOpen(true);
    
    // Enregistrer la visualisation dans l'historique
    await recordHistory(course.id, 'View');
    
    // Afficher une notification
    setSnackbar({
      open: true,
      message: `Visualisation de "${course.titre || course.title || course.nom}" enregistrée`,
      severity: 'info'
    });
  };
  
  /**
   * Ouvrir le QCM d'un cours
   * @param {Object} course - Le cours dont on veut ouvrir le QCM
   */
  const openQcm = (course) => {
    setSelectedCourseForQcm(course);
    setQcmModalOpen(true);
  };

  /**
   * Gérer la complétion du QCM
   * @param {Object} result - Résultat du QCM
   */
  const handleQcmComplete = async (result) => {
    if (studentId) {
      const submitResult = await FormationService.submitQcmResult({
        student_id: studentId,
        course_id: result.courseId,
        score: result.score,
        total_questions: result.totalQuestions,
        correct_answers: result.correctAnswers,
        answers: result.answers
      });
      
      if (submitResult && submitResult.status === true) {
        // Rafraîchir les résultats
        const resultsResult = await FormationService.getResults(studentId);
        if (resultsResult && resultsResult.status === true) {
          setQcmResults(resultsResult.data?.results || []);
        }
        
        setSnackbar({
          open: true,
          message: `QCM terminé ! Score: ${result.score}%`,
          severity: result.score >= 50 ? 'success' : 'warning'
        });
      }
    }
    setQcmModalOpen(false);
  };

  // Couleurs pour les en-têtes des cours
  const courseColors = [
    '#E3F2FD', '#E8F5E9', '#FFF3E0', '#FCE4EC', '#F3E5F5', '#E0F2F1', '#FFEBEE', '#E8EAF6'
  ];

  // Fonction pour obtenir le type normalisé
  const getNormalizedType = (type) => {
    const typeLower = type?.toLowerCase() || '';
    if (typeLower === 'pdf') return 'pdf';
    if (typeLower === 'ppt' || typeLower === 'pptx') return 'ppt';
    if (typeLower === 'mp4' || typeLower === 'video') return 'video';
    return 'default';
  };

  // Icônes selon le type de fichier
  const getFileIcon = (type) => {
    const normalizedType = getNormalizedType(type);
    switch(normalizedType) {
      case 'pdf':
        return <PdfIcon sx={{ fontSize: 40, color: '#D32F2F' }} />;
      case 'ppt':
        return <PptIcon sx={{ fontSize: 40, color: '#ED6C02' }} />;
      case 'video':
        return <VideoIcon sx={{ fontSize: 40, color: '#2E7D32' }} />;
      default:
        return <DescriptionIcon sx={{ fontSize: 40, color: '#1976D2' }} />;
    }
  };

  // Label selon le type de fichier
  const getFileLabel = (type) => {
    const normalizedType = getNormalizedType(type);
    switch(normalizedType) {
      case 'pdf':
        return 'PDF';
      case 'ppt':
        return 'PPT';
      case 'video':
        return 'VIDEO';
      default:
        return 'DOC';
    }
  };

  // Vérifier si un cours est verrouillé
  const isCourseLocked = (course) => {
    if (!course.prerequisite_course_id) return false;
    if (!qcmResults || qcmResults.length === 0) return true;
    const prereqResult = qcmResults.find(r => 
      (r.course_id === course.prerequisite_course_id || r.courseId === course.prerequisite_course_id)
    );
    if (!prereqResult) return true;
    const minScore = course.min_prereq_score || 50;
    return prereqResult.score < minScore;
  };

  // Filtrer les cours
  const filteredCourses = courses?.filter(course => {
    const matchesSearch = searchTerm === '' || 
      (course.titre || course.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (course.matiere_nom || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMatiere = selectedMatiere === '' || course.matiere_id === parseInt(selectedMatiere);
    return matchesSearch && matchesMatiere;
  }) || [];

  // Grouper par matière
  const groupedCourses = Object.entries(
    filteredCourses.reduce((acc, course) => {
      const matiereNom = course.matiere_nom || 'Autres';
      if (!acc[matiereNom]) acc[matiereNom] = [];
      acc[matiereNom].push(course);
      return acc;
    }, {})
  );

  // Pagination des matières
  const totalPages = Math.ceil(groupedCourses.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, groupedCourses.length);
  const currentMatieres = groupedCourses.slice(startIndex, startIndex + itemsPerPage);

  // Fonction pour obtenir le résultat d'un cours
  const getCourseResult = (courseId) => {
    return qcmResults?.find(r => r.course_id === courseId || r.courseId === courseId);
  };

  const getScoreColor = (score) => {
    return score >= 50 ? '#2E7D32' : '#D32F2F';
  };

  const getScoreBgColor = (score) => {
    return score >= 50 ? '#E8F5E9' : '#FFEBEE';
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <FormationMainContainer {...headerBanqueCV}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <Stack spacing={2} alignItems="center">
              <LinearProgress sx={{ width: 200 }} />
              <Typography variant="body2" color="textSecondary">
                Chargement de vos cours...
              </Typography>
            </Stack>
          </Box>
        </Container>
      </FormationMainContainer>
    );
  }

  // Obtenir le prénom de l'étudiant
  const getStudentFirstName = () => {
    const storedUserDetails = localStorage.getItem('USER_DETAILS');
    if (storedUserDetails && storedUserDetails !== 'undefined' && storedUserDetails !== 'null') {
      try {
        const userData = JSON.parse(storedUserDetails);
        if (userData.first_name) return userData.first_name;
        if (userData.prenom) return userData.prenom;
        if (userData.name) return userData.name.split(' ')[0];
      } catch (parseError) {
        return 'Candidat';
      }
    }
    return 'Candidat';
  };

  return (
    <FormationMainContainer {...headerBanqueCV}>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Hero Section */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1976D2 0%, #0288D1 100%)',
            borderRadius: '20px',
            p: 4,
            mb: 4,
            position: 'relative',
            overflow: 'hidden',
            color: '#FFFFFF',
            boxShadow: '0 4px 20px rgba(25,118,210,0.25)',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" position="relative" zIndex={1}>
            <Box>
              <Typography variant="h4" fontWeight="800" gutterBottom>
                Mes cours, {getStudentFirstName()} 👋
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Retrouvez tous vos cours et suivez votre progression
              </Typography>
            </Box>
            <StarsIcon sx={{ fontSize: 64, opacity: 0.8 }} />
          </Box>
        </Box>

        {/* Barre de recherche et filtre */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4 }}>
          <TextField
            fullWidth
            placeholder="Rechercher un cours par titre ou matière..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#9E9E9E' }} />
                </InputAdornment>
              ),
              sx: { borderRadius: '12px', bgcolor: '#FFFFFF' }
            }}
            sx={{ flex: 1 }}
          />
          <FormControl sx={{ minWidth: 200 }}>
            <Select
              value={selectedMatiere}
              onChange={(e) => setSelectedMatiere(e.target.value)}
              displayEmpty
              sx={{ borderRadius: '12px', bgcolor: '#FFFFFF' }}
            >
              <MenuItem value="">Toutes les matières</MenuItem>
              {matieres?.map((m) => (
                <MenuItem key={m.id} value={m.id}>{m.nom}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {/* En-tête avec pagination */}
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h6" fontWeight="bold" display="flex" alignItems="center" gap={1}>
            <AccessTimeIcon sx={{ color: '#1976D2' }} />
            Cours par matière
          </Typography>
          
          <Box display="flex" alignItems="center" gap={2}>
            {totalPages > 1 && (
              <>
                <IconButton
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  size="small"
                  sx={{
                    bgcolor: currentPage === 1 ? '#F5F5F5' : '#1976D2',
                    color: currentPage === 1 ? '#9E9E9E' : 'white',
                    '&:hover': {
                      bgcolor: currentPage === 1 ? '#F5F5F5' : '#1565C0'
                    }
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>
                
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Matières {startIndex + 1}-{endIndex} sur {groupedCourses.length}
                </Typography>
                
                <IconButton
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  size="small"
                  sx={{
                    bgcolor: currentPage === totalPages ? '#F5F5F5' : '#1976D2',
                    color: currentPage === totalPages ? '#9E9E9E' : 'white',
                    '&:hover': {
                      bgcolor: currentPage === totalPages ? '#F5F5F5' : '#1565C0'
                    }
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </>
            )}
          </Box>
        </Box>

        {/* Liste des matières */}
        {currentMatieres.length === 0 ? (
          <Paper sx={{ textAlign: 'center', p: 8, borderRadius: '16px', bgcolor: '#F9FAFB' }}>
            <BookIcon sx={{ fontSize: 64, color: '#9E9E9E', opacity: 0.5, mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Aucun cours trouvé
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Essayez de modifier vos critères de recherche
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {currentMatieres.map(([matiere, coursListe]) => (
              <Grid item xs={12} md={6} lg={4} key={matiere}>
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  {/* En-tête matière */}
                  <Paper
                    sx={{
                      p: 2.5,
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ width: '100%' }} 
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <BookmarkIcon sx={{ color: '#1976D2' }} />
                        <Typography fontWeight="bold">
                          {matiere}
                        </Typography>
                      </Stack>
                      <Chip
                        label={`${coursListe.length} cours`}
                        size="small"
                        color="primary"
                      />
                    </Stack>
                  </Paper>
                  
                  {/* Liste des cours */}
                  <Box sx={{ flexGrow: 1 }}>
                    <Stack spacing={2}>
                      {coursListe.map((course, idx) => {
                        const colorIndex = idx % courseColors.length;
                        const result = getCourseResult(course.id);
                        const isLocked = isCourseLocked(course);
                        const fileType = course.type || course.file_type || 'default';
                        const fileIcon = getFileIcon(fileType);
                        const fileLabel = getFileLabel(fileType);
                        const courseTitle = course.titre || course.title || course.nom || `Cours ${idx + 1}`;
                        const courseSummary = course.summary || course.description || '';
                        
                        return (
                          <Card
                            key={course.id || idx}
                            sx={{
                              height: '100%',
                              display: 'flex',
                              borderRadius: '15px',
                              flexDirection: 'column',
                              transition: 'all 0.3s ease',
                              backgroundColor: '#FFFFFF',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: 4
                              }
                            }}
                          >
                            <Box
                              sx={{
                                height: 100,
                                bgcolor: courseColors[colorIndex],
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                                borderBottom: '1px solid',
                                borderColor: 'divider'
                              }}
                            >
                              {fileIcon}
                              {isLocked && (
                                <Chip
                                  icon={<LockIcon />}
                                  label="Verrouillé"
                                  size="small"
                                  sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    bgcolor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '& .MuiChip-icon': { color: '#FFFFFF' }
                                  }}
                                />
                              )}
                              {/* Badge du type de fichier */}
                              <Chip
                                label={fileLabel}
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  bottom: 8,
                                  right: 8,
                                  bgcolor: 'rgba(0,0,0,0.7)',
                                  color: '#FFFFFF',
                                  fontWeight: 'bold',
                                  fontSize: '0.7rem',
                                  letterSpacing: '0.5px'
                                }}
                              />
                            </Box>
                            
                            <CardContent sx={{ flexGrow: 1, p: 2 }}>
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
                                <Chip
                                  label={course.matiere_nom}
                                  size="small"
                                  sx={{ bgcolor: '#E3F2FD', color: '#1976D2', fontWeight: 600 }}
                                />
                                {result && (
                                  <Chip
                                    icon={result.score >= 50 ? <CheckCircleIcon /> : <CancelIcon />}
                                    label={`${result.score}%`}
                                    size="small"
                                    sx={{
                                      bgcolor: getScoreBgColor(result.score),
                                      color: getScoreColor(result.score),
                                      fontWeight: 600
                                    }}
                                  />
                                )}
                              </Stack>
                              
                              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                {courseTitle}
                              </Typography>
                              
                              {courseSummary && (
                                <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.5 }}>
                                  {courseSummary.length > 80 
                                    ? `${courseSummary.substring(0, 80)}...` 
                                    : courseSummary}
                                </Typography>
                              )}
                              
                              {/* Afficher le prérequis si le cours est verrouillé */}
                              {isLocked && course.prerequisite_course && (
                                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                  🔒 Prérequis: {course.prerequisite_course.title || 'Cours précédent'} ({course.min_prereq_score || 50}% requis)
                                </Typography>
                              )}
                            </CardContent>
                            
                            <CardActions sx={{ p: 2, pt: 0, display: "flex", gap: 1.5 }}>
                              <Button
                                variant="outlined"
                                startIcon={<VisibilityIcon />}
                                onClick={() => openCourse(course)}
                                disabled={isLocked}
                                sx={{
                                  flex: 2,
                                  minHeight: 52,
                                  borderRadius: "12px",
                                  textTransform: "none",
                                  fontWeight: 600,
                                  fontSize: "0.95rem",
                                  borderWidth: 2,
                                  borderColor: "#1976D2",
                                  color: "#1976D2",
                                  transition: "all 0.3s ease",
                                  "&:hover": {
                                    borderColor: "#1565C0",
                                    backgroundColor: "rgba(25,118,210,0.05)",
                                    transform: "translateY(-2px)"
                                  }
                                }}
                              >
                                Voir la leçon
                              </Button>
                              <Button
                                variant="contained"
                                onClick={() => openQcm(course)}
                                sx={{
                                  flex: 1,
                                  minWidth: 110,
                                  minHeight: 52,
                                  borderRadius: "12px",
                                  textTransform: "none",
                                  fontWeight: 600,
                                  fontSize: "0.95rem",
                                  background: "linear-gradient(135deg, #4CAF50, #45a049)",
                                  boxShadow: "0 4px 12px rgba(76,175,80,0.25)",
                                  transition: "all 0.3s ease",
                                  "&:hover": {
                                    background: "linear-gradient(135deg, #45a049, #3d8b40)",
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 8px 18px rgba(76,175,80,0.35)"
                                  },
                                  "&:active": {
                                    transform: "translateY(0)"
                                  }
                                }}
                              >
                                QCM
                              </Button>
                            </CardActions>
                          </Card>
                        );
                      })}
                    </Stack>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Modals */}
      <CourseViewer 
        open={courseViewerOpen}
        course={selectedCourse}
        onClose={() => setCourseViewerOpen(false)}
      />
      
      <QcmModal
        open={qcmModalOpen}
        course={selectedCourseForQcm}
        onClose={() => setQcmModalOpen(false)}
        onComplete={handleQcmComplete}
      />

      {/* Snackbar pour les notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </FormationMainContainer>
  );
};

export default MesCours;